import { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, ExternalLink, BookOpen, ArrowRight, ArrowLeft, AlertTriangle, BellRing, Lock } from 'lucide-react';
import SiteDisclaimer from '@/components/SiteDisclaimer';
import WatchlistStarButton from '@/components/WatchlistStarButton';
import ShareToXButton from '@/components/ShareToXButton';
import IpoAlertSettings from '@/components/IpoAlertSettings';
import PlusUpsellModal from '@/components/PlusUpsellModal';
import { useAuth } from '@/contexts/AuthContext';
import { useTableControls, SortableTh, TableToolbar, PaginationBar } from '@/components/SortableTableControls';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IPO_SEARCH_FIELDS = [
  { key: 'ticker', label: 'Ticker', get: (r) => r.ticker },
  { key: 'issuer', label: 'Issuer', get: (r) => r.issuer_name },
];

/**
 * IpoWatchPage — public SEO landing surface at /ipo-watch.
 *
 * Round 98j (Jul 2026) — Pass 1 scope: cron + endpoint + page.
 * Pass 2 (deferred): user-set bell alerts (matches Dividend Calendar
 * pattern), auto-classify pipeline, header nav badge.
 */
export default function IpoWatchPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin'; // PAYWALL FLIP: admin || plus
  const [upsellOpen, setUpsellOpen] = useState(false);
  // Round 98l (Jul 2026) — Initialize state from URL params so
  // shareable links (Twitter/Discord/Reddit) hydrate the exact view
  // that was shared. Falls back to defaults when params are missing
  // or invalid. Also gives Google more indexable variations of the
  // page — `?window=6m` and `?window=6m&showAll=true` count as
  // distinct URLs to a crawler.
  const [data, setData] = useState(null);
  const [window, setWindow] = useState(() => {
    try {
      const w = new URLSearchParams(globalThis.location?.search || '').get('window');
      return ['1m', '3m', '6m'].includes(w) ? w : '3m';
    } catch { return '3m'; }
  });
  const [showAll, setShowAll] = useState(() => {
    try {
      return new URLSearchParams(globalThis.location?.search || '').get('showAll') === 'true';
    } catch { return false; }
  });
  const [loading, setLoading] = useState(true);
  // Round 98n (Jul 2026) — IPO Alerts settings disclosure. Matches
  // the Dividend Calendar disclosure-panel pattern (panel opens
  // directly under the Alerts pill, not as a floating modal).
  const [alertSettingsOpen, setAlertSettingsOpen] = useState(false);

  // Jul 2026 (Plus paywall) — the All-IPOs universe is Plus-gated.
  // Force free users back to dividend-only even if ?showAll=true was
  // shared in a link (admin bypass; auth may resolve after mount).
  useEffect(() => {
    if (showAll && !isAdmin) setShowAll(false);
  }, [showAll, isAdmin]);

  // Round 98l — Push filter state back into the URL (using
  // history.replaceState so browser back-button isn't polluted).
  // Fires on window or showAll change.
  useEffect(() => {
    try {
      const p = new URLSearchParams();
      if (window !== '3m') p.set('window', window);
      if (showAll) p.set('showAll', 'true');
      const qs = p.toString();
      const newUrl = qs ? `/ipo-watch?${qs}` : '/ipo-watch';
      globalThis.history?.replaceState?.(null, '', newUrl);
    } catch { /* SSR-safe */ }
  }, [window, showAll]);

  useEffect(() => {
    document.title = 'IPO Watch — New REIT, CEF, BDC, MLP, Preferred & Growth IPOs | TwylightCrow';
    const canonical = 'https://twylightcrow.com/ipo-watch';
    const ogTitle = 'IPO Watch — New REIT, CEF, BDC, MLP, Preferred & Growth IPOs';
    const ogDesc = 'Track new IPOs from the last 30/90/180 days — REITs, CEFs, BDCs, MLPs, Preferred stocks, SPACs, and growth IPOs. SEC-sourced, refreshed nightly. Includes SPAC Volume Signal for market-froth context.';
    const ogImage = 'https://twylightcrow.com/dividrip-icon.png?v=3';

    const setMetaByName = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute('name', name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    const setMetaByProp = (property, content) => {
      let el = document.querySelector(`meta[property="${property}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute('property', property); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };

    setMetaByName('description', 'Track new REIT, CEF, BDC, MLP, and Preferred stock IPOs from the last 30/90/180 days. SEC-sourced listings, refreshed nightly. Educational tool for income investors — not financial advice.');
    setMetaByName('keywords', 'dividend IPO, REIT IPO, CEF IPO, BDC IPO, preferred stock IPO, new dividend stocks, upcoming IPOs, closed-end fund IPO, MLP IPO');

    // Open Graph — Round 98m
    setMetaByProp('og:type', 'website');
    setMetaByProp('og:site_name', 'TwylightCrow — DiviDrip');
    setMetaByProp('og:url', canonical);
    setMetaByProp('og:title', ogTitle);
    setMetaByProp('og:description', ogDesc);
    setMetaByProp('og:image', ogImage);
    setMetaByProp('og:image:alt', 'IPO Watch on TwylightCrow');

    // Twitter / X
    setMetaByName('twitter:card', 'summary');
    setMetaByName('twitter:title', ogTitle);
    setMetaByName('twitter:description', ogDesc);
    setMetaByName('twitter:image', ogImage);
    setMetaByName('twitter:image:alt', 'IPO Watch on TwylightCrow');

    let link = document.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement('link'); link.setAttribute('rel', 'canonical'); document.head.appendChild(link); }
    link.setAttribute('href', canonical);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/pages/ipo-watch`, { params: { window } })
      .then((r) => { if (!cancelled) { setData(r.data); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [window]);

  const rows = useMemo(() => {
    if (!data) return [];
    return showAll ? (data.all_ipos || []) : (data.dividend_ipos || []);
  }, [data, showAll]);

  const fmt$ = (n) => n == null ? '—' : n >= 1e9 ? `$${(n/1e9).toFixed(1)}B` : n >= 1e6 ? `$${(n/1e6).toFixed(0)}M` : `$${n.toFixed(0)}`;
  const secTypeBadge = (r) => {
    if (r.is_preferred) return { label: 'PFD',  tone: 'bg-purple-500/15 text-purple-700 dark:text-purple-300 ring-purple-500/40' };
    if (r.is_reit)      return { label: 'REIT', tone: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 ring-emerald-500/40' };
    if (r.is_bdc)       return { label: 'BDC',  tone: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 ring-amber-500/40' };
    if (r.is_cef)       return { label: 'CEF',  tone: 'bg-cyan-500/15 text-cyan-700 dark:text-cyan-300 ring-cyan-500/40' };
    if (r.is_dividend_ipo) return { label: 'DIV', tone: 'bg-sky-500/15 text-sky-700 dark:text-sky-300 ring-sky-500/40' };
    if ((r.security_type || '').toUpperCase() === 'SP') return { label: 'SPAC', tone: 'bg-muted/40 text-muted-foreground ring-muted-foreground/20' };
    return { label: r.security_type || '—', tone: 'bg-muted/40 text-muted-foreground ring-muted-foreground/20' };
  };
  const statusColor = (s) =>
    s === 'new'      ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' :
    s === 'pending'  ? 'bg-amber-500/15 text-amber-700 dark:text-amber-300' :
    s === 'history'  ? 'bg-muted/40 text-muted-foreground' :
                       'bg-muted/40 text-muted-foreground';

  // Jul 2026 — search + column sort + 25-row pagination (client-side).
  // Missing values ('—' / null) always sort as the LOWEST.
  const sortAccessors = useMemo(() => ({
    ticker: { get: (r) => r.ticker, type: 'str' },
    issuer: { get: (r) => r.issuer_name, type: 'str' },
    listed: { get: (r) => r.listing_date, type: 'str' },
    type: { get: (r) => secTypeBadge(r).label, type: 'str' },
    status: { get: (r) => r.ipo_status, type: 'str' },
    offer: { get: (r) => r.total_offer_size, type: 'num' },
    price: { get: (r) => r.final_issue_price, type: 'num' },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);
  const ctrl = useTableControls({
    rows,
    searchFields: IPO_SEARCH_FIELDS,
    sortAccessors,
    pageSize: 25,
  });

  return (
    <div className="min-h-screen bg-background">
      <main className="max-w-6xl mx-auto px-4 sm:px-6 pt-8 sm:pt-12 pb-0" data-testid="ipo-watch-page">
        <button type="button" onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="ipo-watch-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <header className="mb-10">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-700 dark:text-purple-300 text-[10px] font-bold uppercase tracking-widest mb-3">
              <Sparkles className="w-3 h-3" /> Refreshed Nightly · SEC-Sourced
            </div>
            <div className="flex items-center gap-2">
              {/* Round 98n — Alerts pill. Toggles the settings
                  disclosure panel below the hero. Copies the exact
                  visual + interaction pattern from DividendCalendar.js
                  line 492-505 so the two "Alerts" affordances feel
                  identical across surfaces. */}
              <button
                type="button"
                onClick={() => setAlertSettingsOpen((v) => !v)}
                className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-md border transition-colors ${
                  alertSettingsOpen
                    ? 'border-violet-500 bg-violet-600 text-white hover:bg-violet-700'
                    : 'border-violet-300 dark:border-violet-700 bg-violet-50 dark:bg-violet-950/30 text-violet-700 dark:text-violet-300 hover:bg-violet-100 dark:hover:bg-violet-900/40'
                }`}
                data-testid="ipo-alerts-settings-toggle"
                title="Configure IPO alert pop-ups"
              >
                <BellRing className="w-3.5 h-3.5" />
                Alerts
              </button>
              {/* Round 98m — page-level Share-to-X using backend OG
                  unfurl URL so social crawlers render a rich card. */}
              <ShareToXButton
                variant="button"
                url="https://twylightcrow.com/api/share/og-page/ipo-watch"
                text="IPO Watch — new REIT, CEF, BDC, MLP, Preferred, SPAC & growth IPOs from the SEC pipeline. Refreshed nightly:"
                hashtags={['DividendIPO', 'IncomeInvesting', 'REIT']}
                ticker="ipo-watch"
              />
            </div>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-foreground leading-[1.05]">
            IPO Watch
          </h1>
          {/* Round 98n — Alert settings disclosure panel. Opens BELOW
              the Alerts pill above (not a floating overlay) so users
              can see the settings + preview + the live table at the
              same time. */}
          {alertSettingsOpen && (
            <div className="mt-4 rounded-xl border-2 border-violet-500/30 bg-card p-4 max-w-2xl" data-testid="ipo-alerts-settings-panel">
              <IpoAlertSettings
                onClose={() => setAlertSettingsOpen(false)}
                onOpenStock={(t) => navigate(`/stock/${t}`)}
              />
            </div>
          )}
          <p className="mt-4 text-lg text-muted-foreground max-w-3xl leading-relaxed">
            {loading ? 'Loading IPO pipeline…' : (
              <>
                <span className="font-semibold text-foreground">{data?.dividend_ipos_count ?? '—'}</span> dividend-classified IPOs
                out of <span className="font-semibold text-foreground">{data?.total_ipos ?? '—'}</span> total listings in the last{' '}
                {window === '1m' ? '30 days' : window === '3m' ? '90 days' : '180 days'}.
                Track new REIT, CEF, BDC, MLP, and Preferred stock launches — the earliest income-investing signals.
              </>
            )}
          </p>
        </header>

        {/* Editorial body — dual thesis: CEF IPO discount alpha + broader income-investor angle */}
        <section className="prose prose-slate dark:prose-invert max-w-none mb-8 text-[15px] leading-relaxed">
          <h2 className="text-xl font-bold mb-3">Why income investors hunt IPOs</h2>
          <p className="text-muted-foreground">
            IPOs are dominated by SPACs and pre-revenue growth stories, but hidden in the noise are the two IPO patterns
            income investors actually care about: <strong>new REIT / BDC / MLP listings</strong> (a fresh vehicle
            debuting with a stated payout schedule and a clean cap-table) and <strong>new Preferred Stock / Baby Bond
            issues</strong> (fixed-income substitutes trading on stock exchanges with often-attractive coupons in the
            first weeks of secondary trading). Because these launches drop into thinner liquidity than seasoned
            issues, disciplined income investors can occasionally pick them up at meaningful discounts.
          </p>
          <p className="text-muted-foreground mt-3">
            The most well-documented alpha source is the <strong>CEF IPO discount-widening pattern</strong>: newly-listed
            closed-end funds almost universally begin trading at their NAV (or close to it), then drift to a 5–15%
            discount over the first 6–12 months as the initial underwriter&apos;s promotional support fades. Buying
            AFTER the discount widens — not at IPO — has historically delivered 4–8% of &quot;free&quot; return that
            an equivalent NAV-tracking mutual fund couldn&apos;t offer. This page surfaces new CEF listings so you
            can watch that discount develop instead of chasing the IPO itself.
          </p>
          <p className="text-muted-foreground mt-3">
            More broadly: knowing what launched matters even if you never buy. New REIT IPOs signal sub-sector supply
            expansion (bad for existing REIT dividend growth). Wave of new BDC listings usually correlate with peak
            credit cycles (a warning). Preferred stock IPO volume tracks corporate refinancing needs. All of these
            data points sit weeks or months upstream of the dividend headlines they eventually create.
          </p>
        </section>

        {/* Investor advisory disclaimer — user asked for prominent placement */}
        <div className="mb-8 rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 flex items-start gap-3" data-testid="ipo-watch-disclaimer">
          <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
          <div className="text-sm text-foreground">
            <p className="font-semibold mb-1">Educational Tool — Not Financial Advice</p>
            <p className="text-muted-foreground">
              This page tracks publicly-available SEC IPO filings for research and educational purposes only. IPOs
              are high-risk investments — many trade below their offering price within 12 months, and dividend-security
              IPOs (especially CEFs) have well-documented post-IPO discount-widening patterns that can produce losses
              for early buyers. Nothing on this page is a recommendation to buy, sell, or hold any security. Consult
              with a qualified financial advisor before making investment decisions that could result in loss of capital.
            </p>
          </div>
        </div>

        {/* Round 98k (Jul 2026) — SPAC Volume Signal card. Data-native
            market-froth gauge. When SPAC IPO volume spikes (>20 in
            30d), historically it's a top-of-cycle warning for dividend
            investors: overheated risk appetite, capital chasing yield
            substitutes, credit spreads compressed. When it collapses
            (<5 in 30d), the reverse — flight to quality favouring
            dividends. All three columns clickable to reveal the
            underlying SPAC list. */}
        {data?.spac_volume && (
          <div className="mb-8 rounded-lg border border-border p-4 bg-muted/10" data-testid="spac-volume-signal">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1">SPAC Volume Signal</div>
                <div className="text-xs text-muted-foreground max-w-xl">Market-froth gauge. Spikes correlate with cycle tops (risk-on, capital chasing yield substitutes); collapses correlate with flights to quality — a tailwind for dividend investors.</div>
              </div>
              {(() => {
                const d30 = data.spac_volume.d30;
                const froth = d30 >= 20 ? { label: 'HOT', tone: 'bg-rose-500/15 text-rose-700 dark:text-rose-300 ring-rose-500/40' }
                            : d30 >= 10 ? { label: 'ELEVATED', tone: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 ring-amber-500/40' }
                            : d30 >= 5  ? { label: 'NORMAL', tone: 'bg-sky-500/15 text-sky-700 dark:text-sky-300 ring-sky-500/40' }
                                        : { label: 'COLD', tone: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 ring-emerald-500/40' };
                return <span className={`inline-flex items-center px-2 py-0.5 rounded-full ring-1 text-[10px] font-bold uppercase tracking-wider ${froth.tone}`}>{froth.label}</span>;
              })()}
            </div>
            <div className="grid grid-cols-3 gap-3">
              {[
                { k: 'd30',  label: 'Last 30 days',  val: data.spac_volume.d30 },
                { k: 'd90',  label: 'Last 90 days',  val: data.spac_volume.d90 },
                { k: 'd180', label: 'Last 180 days', val: data.spac_volume.d180 },
              ].map(c => (
                <div key={c.k} className="text-center rounded-md border border-border bg-background p-3" data-testid={`spac-volume-${c.k}`}>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{c.label}</div>
                  <div className="text-3xl font-extrabold font-mono mt-1 tabular-nums">{c.val}</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">SPACs</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Window + show-all controls */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mr-2">Window:</div>
          {[
            { key: '1m', label: '1 Month' },
            { key: '3m', label: '3 Months' },
            { key: '6m', label: '6 Months' },
          ].map(w => (
            <button key={w.key} type="button" onClick={() => setWindow(w.key)}
              className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-all ${
                window === w.key ? 'bg-sky-600 text-white border-sky-600'
                                 : 'bg-background text-foreground border-border hover:border-sky-500/40'
              }`}
              data-testid={`ipo-window-${w.key}`}>{w.label}</button>
          ))}
          {/* Round 98j — Live count caption for the currently-selected
              window. Reflects whichever view mode is active (dividend
              only vs all IPOs). Cheaper than pre-fetching all 3
              windows to badge each button. */}
          <span className="ml-1 text-xs text-muted-foreground font-mono" data-testid="ipo-window-count">
            {loading ? '…'
              : `→ ${(showAll ? data?.total_ipos : data?.dividend_ipos_count) ?? 0} ${showAll ? 'IPOs' : 'dividend IPOs'}`}
          </span>
          <div className="flex-1" />
          <button type="button"
            onClick={() => {
              if (!showAll && !isAdmin) { setUpsellOpen(true); return; }
              setShowAll(!showAll);
            }}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold border transition-all ${
              showAll ? 'bg-purple-600 text-white border-purple-600'
                      : 'bg-background text-foreground border-border hover:border-purple-500/40'
            }`}
            data-testid="ipo-show-all-toggle">
            {!showAll && !isAdmin && (
              <Lock className="w-3.5 h-3.5 text-yellow-600 dark:text-yellow-400" data-testid="ipo-show-all-lock" />
            )}
            {showAll
              ? `Show Dividend Only — ${data?.dividend_ipos_count ?? 0}`
              : `Show All IPOs (incl. SPACs) — ${data?.total_ipos ?? 0}`}
          </button>
          <PlusUpsellModal open={upsellOpen} onClose={() => setUpsellOpen(false)} />
        </div>

        {/* Freshly listed card — status=new in last 30 days. Jul 2026:
            follows the Show-All toggle (dividend list for the free
            default view, full universe when Show All is active) and
            hides entirely when the active list is empty. Includes a
            Share-to-X per tile, same as the Founder-Led fresh card. */}
        {(() => {
          const freshRows = (showAll ? data?.freshly_new_all : data?.freshly_new) || [];
          if (freshRows.length === 0) return null;
          return (
          <div className="mb-6 rounded-lg border border-emerald-500/25 bg-gradient-to-br from-emerald-500/5 via-transparent to-transparent p-4" data-testid="fresh-ipo-card">
            <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-emerald-700 dark:text-emerald-300 mb-2">
              <Sparkles className="w-3 h-3" /> Freshly Listed — Last 30 Days
              <span className="normal-case tracking-normal font-mono text-muted-foreground">
                · {freshRows.length}{showAll ? ' (all IPOs)' : ' dividend'}
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {freshRows.map(f => (
                <div key={f.ticker}
                  className={`relative rounded-md border border-border bg-background p-2.5 transition-all ${f.in_cache ? 'hover:border-emerald-500/50 cursor-pointer' : ''}`}
                  onClick={() => f.in_cache && navigate(`/stock/${f.ticker}`)}
                  data-testid={`fresh-ipo-${f.ticker}`}>
                  {/* Round 98l — Watchlist star in the top-right corner.
                      Feb 2026 — Only shown when the ticker exists in
                      stock_cache so users can't watchlist a stock the
                      Watchlist page can't render. */}
                  {f.in_cache && (
                    <div className="absolute top-1 right-1" onClick={(e) => e.stopPropagation()}>
                      <WatchlistStarButton ticker={f.ticker} size="w-3.5 h-3.5" />
                    </div>
                  )}
                  <div className="flex items-baseline justify-between gap-2 pr-6">
                    <span className="font-bold text-emerald-700 dark:text-emerald-300 text-sm">{f.ticker}</span>
                    <span className="font-mono text-xs text-muted-foreground">{f.listing_date}</span>
                  </div>
                  <div className="text-[11px] text-muted-foreground truncate mt-0.5" title={f.issuer_name}>{f.issuer_name}</div>
                  <div className="flex justify-end mt-1" onClick={(e) => e.stopPropagation()}>
                    <ShareToXButton
                      variant="button"
                      label="Share to X"
                      size="w-3 h-3"
                      ticker={f.ticker}
                      url="https://twylightcrow.com/api/share/og-page/ipo-watch"
                      text={`${f.is_dividend_ipo ? 'New dividend payer just listed' : 'Freshly listed'}: $${f.ticker} (${f.issuer_name || 'new IPO'}) — spotted on the SEC-sourced IPO Watch radar:`}
                      hashtags={['IPO', 'Dividends']}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
          );
        })()}

        {/* Table — searchable (Ticker / Issuer), sortable headers with
            direction chevrons, 25 rows per page. */}
        <TableToolbar
          ctrl={ctrl}
          searchFields={IPO_SEARCH_FIELDS}
          placeholder="Search IPOs…"
          testIdPrefix="ipo-watch"
        />
        <div className="border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="ipo-watch-table">
              <thead className="bg-muted/40 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="w-8 px-2 py-2" aria-label="Watchlist"></th>
                  <SortableTh ctrl={ctrl} sortKey="ticker" label="Ticker" testIdPrefix="ipo-watch" />
                  <SortableTh ctrl={ctrl} sortKey="issuer" label="Issuer" testIdPrefix="ipo-watch" />
                  <SortableTh ctrl={ctrl} sortKey="listed" label="Listed" testIdPrefix="ipo-watch" />
                  <SortableTh ctrl={ctrl} sortKey="type" label="Type" align="center" testIdPrefix="ipo-watch" />
                  <SortableTh ctrl={ctrl} sortKey="status" label="Status" align="center" testIdPrefix="ipo-watch" />
                  <SortableTh ctrl={ctrl} sortKey="offer" label="Offer Size" align="right" testIdPrefix="ipo-watch" />
                  <SortableTh ctrl={ctrl} sortKey="price" label="Issue Price" align="right" testIdPrefix="ipo-watch" />
                  <th className="w-10 px-2 py-2" aria-label="Share"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {/* Only show "Loading..." when there are no rows to
                    display — otherwise the extra row causes a
                    single-row height flash while switching windows. */}
                {loading && rows.length === 0 && <tr><td colSpan={9} className="text-center py-6 text-muted-foreground">Loading…</td></tr>}
                {!loading && ctrl.pageRows.length === 0 && (
                  <tr><td colSpan={9} className="text-center py-6 text-muted-foreground">
                    {showAll ? 'No IPOs in this window.' : 'No dividend-classified IPOs in this window — dividend IPOs are rare. Try "Show All IPOs" or expand to 6 months.'}
                  </td></tr>
                )}
                {ctrl.pageRows.map(r => {
                  const badge = secTypeBadge(r);
                  const shareBlurb = r.is_dividend_ipo
                    ? `$${r.ticker} — new ${badge.label} IPO from ${r.issuer_name || 'issuer'} on ${r.listing_date}. Fresh dividend candidate:`
                    : `$${r.ticker} — new IPO from ${r.issuer_name || 'issuer'} on ${r.listing_date}. Watching the pipeline:`;
                  return (
                    <tr key={r.ticker}
                        className={`hover:bg-muted/30 ${r.in_cache ? 'cursor-pointer' : ''}`}
                        onClick={() => r.in_cache && navigate(`/stock/${r.ticker}`)}
                        data-testid={`ipo-row-${r.ticker}`}>
                      <td className="px-2 py-2.5 text-center" onClick={(e) => e.stopPropagation()}>
                        {/* Feb 2026 — Only render the watchlist star for
                            tickers we actually have cached data for.
                            Without `in_cache`, the star lets a user add
                            the ticker but Watchlist page silently drops
                            it (no cached row to render), which caused
                            the count-vs-visible-items mismatch you
                            reported. Same gate as the row's clickable
                            navigate — no data, no star, no misleading
                            counter bump. */}
                        {r.in_cache && <WatchlistStarButton ticker={r.ticker} />}
                      </td>
                      <td className="px-3 py-2.5 font-bold text-sky-700 dark:text-sky-300">{r.ticker}</td>
                      <td className="px-3 py-2.5">
                        <div className="max-w-[320px] truncate" title={r.issuer_name}>{r.issuer_name || '—'}</div>
                        {r.sector && <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{r.sector}</div>}
                      </td>
                      <td className="px-3 py-2.5 font-mono text-muted-foreground text-xs">
                        {r.listing_date || '—'}
                        {/* Round 98n (Jul 2026) — UPCOMING badge. Massive
                            returns pending IPOs with future listing_date
                            values; surface them here so the user can spot
                            them at-a-glance. Badge disappears the moment
                            listing_date <= today. */}
                        {r.listing_date && r.listing_date > new Date().toISOString().slice(0, 10) && (
                          <span
                            className="ml-1.5 inline-flex items-center px-1 py-0.5 rounded text-[9px] font-bold uppercase bg-amber-500/15 text-amber-700 dark:text-amber-300 ring-1 ring-amber-500/30 align-middle"
                            data-testid={`ipo-upcoming-badge-${r.ticker}`}
                            title="Upcoming — has not yet listed"
                          >
                            Upcoming
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full ring-1 text-[10px] font-bold uppercase ${badge.tone}`}>{badge.label}</span>
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${statusColor(r.ipo_status)}`}>{r.ipo_status}</span>
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono text-muted-foreground">{fmt$(r.total_offer_size)}</td>
                      <td className="px-3 py-2.5 text-right font-mono">{r.final_issue_price != null ? `$${Number(r.final_issue_price).toFixed(2)}` : '—'}</td>
                      <td className="px-2 py-2.5 text-center">
                        <ShareToXButton
                          ticker={r.ticker}
                          text={shareBlurb}
                          hashtags={r.is_dividend_ipo ? ['DividendIPO', 'IncomeInvesting'] : ['IPO', 'NewListings']}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <div className="mb-10">
          <PaginationBar ctrl={ctrl} testIdPrefix="ipo-watch" />
        </div>

        {/* Screener CTA */}
        <div className="border border-sky-500/30 rounded-lg p-5 mb-10 bg-sky-500/5">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <h3 className="text-lg font-bold mb-1">Screen the dividend universe</h3>
              <p className="text-sm text-muted-foreground">IPO listings are a lead indicator — the full dividend screener has 5,000+ existing tickers filtered by tier, yield, quality, and more.</p>
            </div>
            <Link to="/screener?filter=dividend" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-sky-600 text-white text-sm font-semibold hover:bg-sky-500 transition-colors shrink-0" data-testid="ipo-screener-cta">
              Open Screener <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {/* Related Reading */}
        <section className="border-t border-border pt-8">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2"><BookOpen className="w-5 h-5" /> Related reading</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Learn guides</div>
              <ul className="space-y-2 text-sm">
                <li><Link to="/learn/reits-vs-bdcs-vs-cefs" className="text-sky-700 dark:text-sky-300 hover:underline">REITs vs BDCs vs CEFs — Which Vehicle for Which Investor</Link></li>
                <li><Link to="/learn/mlps-explained" className="text-sky-700 dark:text-sky-300 hover:underline">MLPs Explained — Master Limited Partnership Basics</Link></li>
                <li><Link to="/learn/covered-call-etfs" className="text-sky-700 dark:text-sky-300 hover:underline">Covered Call ETFs — When the Yield Looks Too Good</Link></li>
                <li><Link to="/learn/dividend-traps" className="text-sky-700 dark:text-sky-300 hover:underline">Dividend Traps — How to Spot Them Before They Cut</Link></li>
                <li><Link to="/learn/first-10-tickers" className="text-sky-700 dark:text-sky-300 hover:underline">Your First 10 Dividend Tickers — Building the Base</Link></li>
              </ul>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Glossary terms</div>
              <ul className="space-y-2 text-sm">
                <li><a href="/glossary#entry-cef-closed-end-fund" target="divdrip-glossary" className="inline-flex items-center gap-1 text-sky-700 dark:text-sky-300 hover:underline" data-testid="glossary-cef">Closed-End Fund (CEF) <ExternalLink className="w-3 h-3" /></a></li>
                <li><a href="/glossary#entry-bdc-business-development-company" target="divdrip-glossary" className="inline-flex items-center gap-1 text-sky-700 dark:text-sky-300 hover:underline" data-testid="glossary-bdc">Business Development Company (BDC) <ExternalLink className="w-3 h-3" /></a></li>
                <li><a href="/glossary#entry-reit-real-estate-investment-trust" target="divdrip-glossary" className="inline-flex items-center gap-1 text-sky-700 dark:text-sky-300 hover:underline" data-testid="glossary-reit">REIT <ExternalLink className="w-3 h-3" /></a></li>
                <li><a href="/glossary#entry-preferred-stock-pn" target="divdrip-glossary" className="inline-flex items-center gap-1 text-sky-700 dark:text-sky-300 hover:underline" data-testid="glossary-preferred">Preferred Stock <ExternalLink className="w-3 h-3" /></a></li>
              </ul>
            </div>
          </div>
          {data?.computed_at && (
            <p className="text-[10px] text-muted-foreground uppercase tracking-widest mt-6">
              Data refreshed: {new Date(data.computed_at).toLocaleString()}
            </p>
          )}
        </section>
      </main>
      <SiteDisclaimer />
    </div>
  );
}
